import { Platform } from 'react-native';
import { createThumbnail } from 'react-native-create-thumbnail';

type ThumbResult = { success: boolean; path?: string; msg?: string };

// Web implementation using HTML5 video + canvas
const generateWebThumbnail = async (videoUri: string): Promise<ThumbResult> => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return { success: false, msg: 'DOM not available for thumbnail generation' };
  }
  if (!videoUri) {
    return { success: false, msg: 'Invalid video uri' };
  }

  return new Promise<ThumbResult>((resolve) => {
    try {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');

      // For remote URLs, allow CORS-based drawing when possible
      (video as any).crossOrigin = 'anonymous';
      video.src = videoUri;
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';

      let settled = false;
      const cleanup = () => {
        if (video && video.parentNode) {
          video.parentNode.removeChild(video);
        }
      };

      const finish = (result: ThumbResult) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(result);
      };

      const captureFrame = () => {
        try {
          const width = video.videoWidth || 320;
          const height = video.videoHeight || 180;

          if (!width || !height) {
            return finish({ success: false, msg: 'Video has no dimensions' });
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            return finish({ success: false, msg: 'Could not get canvas context' });
          }

          ctx.drawImage(video, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.8);

          if (!dataUrl) {
            return finish({ success: false, msg: 'Could not generate thumbnail data URL' });
          }

          console.log('[Thumbnail][web] Generated thumbnail data URL');
          finish({ success: true, path: dataUrl });
        } catch (err: any) {
          console.log('[Thumbnail][web] Capture failed:', err?.message || err);
          finish({ success: false, msg: err?.message || 'Capture failed' });
        }
      };

      video.onloadeddata = () => {
        try {
          // Seek a little into the video to avoid black frames
          const seekTarget = Math.min(1, video.duration || 1);
          video.currentTime = seekTarget;
        } catch {
          captureFrame();
        }
      };

      video.onseeked = () => {
        captureFrame();
      };

      video.onerror = () => {
        console.log('[Thumbnail][web] Video load error');
        finish({ success: false, msg: 'Video load error' });
      };

      // Fallback timeout in case events never fire
      setTimeout(() => {
        if (!settled) {
          console.log('[Thumbnail][web] Timeout while generating thumbnail');
          finish({ success: false, msg: 'Timeout generating thumbnail' });
        }
      }, 8000);

      // Add to DOM so some browsers will load it reliably
      document.body.appendChild(video);
    } catch (error: any) {
      console.log('[Thumbnail][web] Exception during setup:', error?.message || error);
      resolve({ success: false, msg: error?.message || 'Web thumbnail generation failed' });
    }
  });
};

export const generateVideoThumbnail = async (videoUri: string): Promise<ThumbResult> => {
  try {
    if (!videoUri) return { success: false, msg: 'Invalid video uri' };

    if (Platform.OS === 'web') {
      // Use HTML5 video + canvas to capture a frame and return a data URL
      return await generateWebThumbnail(videoUri);
    }

    console.log('[Thumbnail] Generating thumbnail for URI:', videoUri);
    const { path } = await createThumbnail({ url: videoUri, timeStamp: 1000 });
    if (!path) return { success: false, msg: 'Could not create thumbnail' };

    // Android returns file://, iOS may return different schemes; pass through as-is
    console.log('[Thumbnail] Generated temporary thumbnail at:', path);
    return { success: true, path };
  } catch (error: any) {
    console.log('[Thumbnail] Generation failed:', error?.message || error);
    return { success: false, msg: error?.message || 'Thumbnail generation failed' };
  }
};

export default generateVideoThumbnail;

